import React, { useState, useEffect } from 'react';
import PartnerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db, storage } from '../config/firebase'; // Added storage
import { useAuth } from '../context/AuthContext';
import {
    collection,
    onSnapshot,
    query,
    where,
    updateDoc,
    addDoc,
    serverTimestamp,
    orderBy,
    doc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Added

const QUERY_STATUSES = {
    // Initial states
    PENDING: 'Pending',
    MORE_DETAILS_REQUESTED: 'More Details Requested',
    DETAILS_PROVIDED: 'Details Provided',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',

    // Driver workflow states
    DRIVER_ASSIGNED: 'Driver Assigned',
    DRIVER_ACCEPTED: 'Driver Accepted',
    DRIVER_ON_WAY: 'Driver On Way',
    DRIVER_REACHED: 'Driver Reached',
    DRIVER_VERIFIED: 'Site Verified',
    DRIVER_REPORTED: 'Inspection Completed',

    // Quotation states
    QUOTATION_UPLOADED: 'Quotation Uploaded',
    QUOTATION_ACCEPTED: 'Quotation Accepted',
    QUOTATION_REJECTED: 'Quotation Rejected',

    // Technician workflow states
    TECHNICIAN_ASSIGNED: 'Technician Assigned',
    TECHNICIAN_ACCEPTED: 'Technician Accepted',
    TECHNICIAN_ON_WAY: 'Technician On Way',
    TECHNICIAN_REACHED: 'Technician Reached',
    TECHNICIAN_WORKING: 'Work in Progress',
    TECHNICIAN_COMPLETED: 'Work Completed',

    // Invoice and payment states
    INVOICE_GENERATED: 'Invoice Generated',
    PAYMENT_PENDING: 'Payment Pending',
    PAYMENT_UPLOADED: 'Payment Uploaded',
    PAID: 'Paid',

    // Follow-up states
    FOLLOW_UP_SCHEDULED: 'Follow-up Scheduled',
    FOLLOW_UP_COMPLETED: 'Follow-up Completed',
    CLOSED: 'Closed'
};

const getWorkflowProgress = (status) => {
    const progressMap = {
        [QUERY_STATUSES.PENDING]: 5,
        [QUERY_STATUSES.MORE_DETAILS_REQUESTED]: 10,
        [QUERY_STATUSES.DETAILS_PROVIDED]: 15,
        [QUERY_STATUSES.ACCEPTED]: 20,
        [QUERY_STATUSES.DRIVER_ASSIGNED]: 25,
        [QUERY_STATUSES.DRIVER_ACCEPTED]: 30,
        [QUERY_STATUSES.DRIVER_ON_WAY]: 35,
        [QUERY_STATUSES.DRIVER_REACHED]: 40,
        [QUERY_STATUSES.DRIVER_VERIFIED]: 45,
        [QUERY_STATUSES.DRIVER_REPORTED]: 50,
        [QUERY_STATUSES.QUOTATION_UPLOADED]: 55,
        [QUERY_STATUSES.QUOTATION_ACCEPTED]: 60,
        [QUERY_STATUSES.TECHNICIAN_ASSIGNED]: 65,
        [QUERY_STATUSES.TECHNICIAN_ACCEPTED]: 70,
        [QUERY_STATUSES.TECHNICIAN_ON_WAY]: 75,
        [QUERY_STATUSES.TECHNICIAN_REACHED]: 80,
        [QUERY_STATUSES.TECHNICIAN_WORKING]: 85,
        [QUERY_STATUSES.TECHNICIAN_COMPLETED]: 90,
        [QUERY_STATUSES.INVOICE_GENERATED]: 95,
        [QUERY_STATUSES.PAYMENT_UPLOADED]: 98,
        [QUERY_STATUSES.PAID]: 100,
        [QUERY_STATUSES.FOLLOW_UP_COMPLETED]: 100,
        [QUERY_STATUSES.CLOSED]: 100
    };
    return progressMap[status] || 0;
};

function PartnerQueries() {
    const { currentUser } = useAuth();
    const [queries, setQueries] = useState([]);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // Changed from statusFilter to match Admin
    const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false); // For Create Query

    // New State for Details View matching Admin
    // New State for Details View matching Admin
    const [invoices, setInvoices] = useState([]);
    const [isEditingTime, setIsEditingTime] = useState(false);
    const [editTimeValue, setEditTimeValue] = useState('');
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportDescription, setReportDescription] = useState('');
    const [reportType, setReportType] = useState('general'); // 'general' or 'followup'
    const [followUpImages, setFollowUpImages] = useState([]);
    const [isUploadingReport, setIsUploadingReport] = useState(false);

    // Changing assignment states - Partners generally don't assign drivers/techs unless self-managed?
    // Assuming Partners CAN view assignments but maybe not change them if Admin manages.
    // However, if Partner manages their own team, they might. 
    // For parity, we'll keep the UI but maybe disable if not allowed. 
    // The previous code didn't have assignments. We will enable them if they exist in DB.

    // New Query State
    const [newQuery, setNewQuery] = useState({
        serviceType: '',
        title: '',
        description: '',
        address: '',
        startDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        latitude: '',
        longitude: ''
    });

    // Fetch Queries for this Partner
    useEffect(() => {
        if (!currentUser) return;

        const q = query(
            collection(db, 'queries'),
            where('partnerId', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const queriesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Client-side sort
            queriesList.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                return dateB - dateA;
            });
            setQueries(queriesList);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching queries:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Fetch Invoices when query selected
    useEffect(() => {
        if (selectedQuery) {
            const invoiceQ = query(collection(db, `queries/${selectedQuery.id}/invoices`), orderBy('createdAt', 'desc'));
            const unsub = onSnapshot(invoiceQ, (snap) => {
                setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            });
            return () => unsub();
        } else {
            setInvoices([]);
        }
    }, [selectedQuery]);

    const handleCreateQuery = async (e) => {
        e.preventDefault();
        try {
            const queryData = {
                partnerId: currentUser.uid,
                partnerName: currentUser.displayName || 'Partner',
                serviceType: newQuery.serviceType,
                title: newQuery.title,
                description: newQuery.description,
                location: newQuery.address,
                latitude: parseFloat(newQuery.latitude) || 0,
                longitude: parseFloat(newQuery.longitude) || 0,
                startDate: newQuery.startDate,
                dueDate: newQuery.dueDate,
                status: 'Pending',
                createdBy: currentUser.uid,
                createdRole: 'partner',
                createdAt: serverTimestamp(),
            };

            await addDoc(collection(db, 'queries'), queryData);
            setIsModalOpen(false);
            setNewQuery({
                serviceType: '',
                title: '',
                description: '',
                address: '',
                startDate: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                latitude: '',
                longitude: ''
            });
            alert("Query created successfully!");
        } catch (error) {
            console.error("Error creating query:", error);
            alert("Failed to create query");
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-800';
        const formatted = status.toLowerCase();
        if (formatted.includes('pending')) return 'bg-yellow-100 text-yellow-800';
        if (formatted.includes('completed') || formatted.includes('success') || formatted.includes('paid')) return 'bg-green-100 text-green-800';
        if (formatted.includes('progress') || formatted.includes('way') || formatted.includes('assigned')) return 'bg-blue-100 text-blue-800';
        if (formatted.includes('reject') || formatted.includes('cancel')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        if (timestamp.toDate) return timestamp.toDate().toLocaleDateString();
        if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleDateString();
        return new Date(timestamp).toLocaleDateString();
    };

    const getImageUri = (img) => {
        if (!img) return null;
        return typeof img === 'string' ? img : (img.uri || img.url);
    };

    const getFollowUpTimeRemaining = (query) => {
        if (!query?.followUpAllowedUntil) return 0;
        const now = new Date();
        const deadline = query.followUpAllowedUntil.toDate ? query.followUpAllowedUntil.toDate() : new Date(query.followUpAllowedUntil);
        const diffMs = deadline - now;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        return Math.max(0, diffHours);
    };

    const canCreateFollowUp = (query) => {
        if (!query) return false;
        if (query.followUp) return false;
        if (!query.followUpAllowedUntil) return false;
        const now = new Date();
        const deadline = query.followUpAllowedUntil.toDate ? query.followUpAllowedUntil.toDate() : new Date(query.followUpAllowedUntil);
        return now <= deadline;
    };

    // Actions & Helpers copied from Admin Queries.jsx

    const saveTime = async () => {
        if (!selectedQuery) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), { preferredTime: editTimeValue });
            setSelectedQuery(prev => ({ ...prev, preferredTime: editTimeValue }));
            setIsEditingTime(false);
        } catch (error) {
            console.error("Error updating time:", error);
            alert("Failed to update time");
        }
    };

    const handleAcceptQuotation = async () => {
        if (!window.confirm("Accept this quotation?")) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.QUOTATION_ACCEPTED,
                quotationAcceptedAt: new Date()
            });
            setSelectedQuery(prev => ({
                ...prev,
                status: QUERY_STATUSES.QUOTATION_ACCEPTED,
                quotationAcceptedAt: new Date()
            }));
        } catch (error) {
            console.error("Error accepting quotation:", error);
        }
    };

    const handleRejectQuotation = async () => {
        const reason = window.prompt("Reason for rejecting this quotation:");
        if (!reason) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.QUOTATION_REJECTED,
                quotationRejectedAt: new Date(),
                quotationRejectionReason: reason
            });
            setSelectedQuery(prev => ({
                ...prev,
                status: QUERY_STATUSES.QUOTATION_REJECTED,
                quotationRejectedAt: new Date(),
                quotationRejectionReason: reason
            }));
        } catch (error) {
            console.error("Error rejecting quotation:", error);
        }
    };

    const handleReportImageChange = (e) => {
        if (e.target.files) {
            setFollowUpImages(Array.from(e.target.files));
        }
    };

    const handleReportIssue = async (e) => {
        e.preventDefault();
        if (!selectedQuery) return;

        try {
            setIsUploadingReport(true);
            let imageUrls = [];

            if (followUpImages.length > 0) {
                // Upload images
                for (let i = 0; i < followUpImages.length; i++) {
                    const file = followUpImages[i];
                    const storageRef = ref(storage, `followup_images/${selectedQuery.id}_${Date.now()}_${i}`);
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    imageUrls.push(url);
                }
            }

            if (reportType === 'followup') {
                // Follow-up logic (like Mobile App)
                const followUpData = {
                    followUp: {
                        description: reportDescription,
                        images: imageUrls,
                        createdAt: new Date(),
                        status: 'pending'
                    }
                };
                await updateDoc(doc(db, 'queries', selectedQuery.id), followUpData);
                setSelectedQuery(prev => ({ ...prev, ...followUpData }));
                alert("Follow-up issue reported successfully.");
            } else {
                // General Report logic
                await addDoc(collection(db, 'reports'), {
                    queryId: selectedQuery.id,
                    queryTitle: selectedQuery.title || selectedQuery.serviceType,
                    partnerId: currentUser.uid,
                    partnerName: currentUser.displayName || 'Partner',
                    description: reportDescription,
                    images: imageUrls,
                    status: 'Open',
                    createdAt: serverTimestamp(),
                    type: 'query_issue'
                });
                alert("Issue reported successfully. Support team will contact you.");
            }
            setIsReportModalOpen(false);
            setReportDescription('');
            setFollowUpImages([]);
        } catch (error) {
            console.error("Error reporting issue:", error);
            alert("Failed to report issue");
        } finally {
            setIsUploadingReport(false);
        }
    };

    // Partner can View Invoices. Can they upload? Assume Admin/Manager does uploading. 
    // But keeping it consistent if they need to upload proof or invoices.

    // Filtering
    const filteredQueries = queries.filter(q => {
        const matchesSearch = (q.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (q.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (q.serviceType?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || (q.status?.toLowerCase() || '') === filterStatus.toLowerCase();
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50">
            <PartnerSidebar />
            <div
                className="flex-1 overflow-y-auto transition-all duration-300"
                style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
            >
                <div className="p-6 min-h-screen">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <div className="flex items-center">
                                {isMobile && (
                                    <button
                                        onClick={toggleMobileSidebar}
                                        className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </button>
                                )}
                                <h1 className="text-3xl font-bold text-brand-dark">Queries</h1>
                            </div>
                            <p className="text-gray-600 mt-1">Manage service requests and queries</p>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-brand-golden hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Query
                        </button>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    placeholder="Search queries..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </div>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="in progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="paid">Paid</option>
                            </select>
                        </div>
                    </div>

                    {/* Queries List (Cards) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredQueries.map(item => (
                            <div key={item.id} className="bg-brand-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-bold text-lg text-brand-dark">{item.serviceType || item.title || 'Service Request'}</h3>
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(item.status)}`}>
                                        {item.status}
                                    </span>
                                </div>
                                <p className="text-gray-600 text-sm mb-2">{item.location || 'No location'}</p>
                                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{item.description}</p>

                                <div className="flex justify-between items-center text-xs text-gray-400 border-t pt-4">
                                    <span>{formatDate(item.createdAt || item.startDate)}</span>
                                    <button
                                        onClick={() => setSelectedQuery(item)}
                                        className="text-brand-golden font-bold hover:text-yellow-600"
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredQueries.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No queries found</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Query Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Create New Query</h2>
                        <form onSubmit={handleCreateQuery} className="space-y-4">
                            {/* Form fields same as before */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Service Type</label>
                                <input
                                    type="text"
                                    placeholder="e.g. AC Repair"
                                    value={newQuery.serviceType}
                                    onChange={e => setNewQuery({ ...newQuery, serviceType: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                <input
                                    type="text"
                                    placeholder="Brief title"
                                    value={newQuery.title}
                                    onChange={e => setNewQuery({ ...newQuery, title: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={newQuery.description}
                                    onChange={e => setNewQuery({ ...newQuery, description: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    rows="3"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Address / Location</label>
                                <input
                                    type="text"
                                    value={newQuery.address}
                                    onChange={e => setNewQuery({ ...newQuery, address: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500">Latitude</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={newQuery.latitude}
                                            onChange={e => setNewQuery({ ...newQuery, latitude: e.target.value })}
                                            placeholder="e.g. 25.2048"
                                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500">Longitude</label>
                                        <input
                                            type="number"
                                            step="any"
                                            value={newQuery.longitude}
                                            onChange={e => setNewQuery({ ...newQuery, longitude: e.target.value })}
                                            placeholder="e.g. 55.2708"
                                            className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Enter coordinates or click on the map below to pick a location.</p>
                                <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                                    <iframe
                                        title="Pick location"
                                        width="100%"
                                        height="250"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        allowFullScreen=""
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps/embed/v1/view?key=YOUR_API_KEY&center=${newQuery.latitude || 25.2048},${newQuery.longitude || 55.2708}&zoom=14`}>
                                    </iframe>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                    <input
                                        type="date"
                                        value={newQuery.startDate}
                                        onChange={e => setNewQuery({ ...newQuery, startDate: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                                    <input
                                        type="date"
                                        value={newQuery.dueDate}
                                        onChange={e => setNewQuery({ ...newQuery, dueDate: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                                <button type="submit" className="px-4 py-2 text-black bg-brand-golden rounded-md hover:bg-yellow-600">Create Query</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Query Details Modal - Enhanced with Cards Style */}
            {selectedQuery && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto flex flex-col shadow-2xl relative">

                        {/* Header */}
                        <div className="p-6 pb-2">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1 pr-4">
                                    <h2 className="text-2xl font-bold text-gray-900">{selectedQuery.serviceType || selectedQuery.title}</h2>
                                    <p className="text-xs text-gray-400 mt-1">Query ID: {selectedQuery.id}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedQuery(null)}
                                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(selectedQuery.status)}`}>
                                    {selectedQuery.status}
                                </span>
                                <button
                                    onClick={() => { setReportType('general'); setIsReportModalOpen(true); }}
                                    className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1 rounded text-xs font-bold transition-colors flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    Report Issue
                                </button>
                            </div>
                        </div>

                        <div className="p-6 pt-2 space-y-6">

                            {/* Info Rows */}
                            <div className="flex justify-between text-sm">
                                <div>
                                    <p className="text-gray-500 text-xs font-bold uppercase">Created By</p>
                                    <p className="font-semibold text-gray-900">{selectedQuery.partnerName}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-gray-500 text-xs font-bold uppercase">Start Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(selectedQuery.createdAt || selectedQuery.startDate)}</p>
                                </div>
                            </div>

                            {/* Preferred Time */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <p className="text-gray-500 text-xs font-bold uppercase">Preferred Time</p>
                                    {!isEditingTime && (
                                        <button onClick={() => { setIsEditingTime(true); setEditTimeValue(selectedQuery.preferredTime || ''); }} className="text-yellow-600 text-xs font-bold hover:underline">Edit</button>
                                    )}
                                </div>
                                {isEditingTime ? (
                                    <div className="flex gap-2">
                                        <input type="time" value={editTimeValue} onChange={(e) => setEditTimeValue(e.target.value)} className="border p-1 rounded text-sm" />
                                        <button onClick={saveTime} className="text-green-600 text-xs font-bold">Save</button>
                                        <button onClick={() => setIsEditingTime(false)} className="text-gray-500 text-xs">Cancel</button>
                                    </div>
                                ) : (
                                    <p className="font-medium text-gray-900">{selectedQuery.preferredTime || 'Not Set'}</p>
                                )}
                            </div>

                            {/* Location & Map */}
                            <div>
                                <p className="text-gray-500 text-xs font-bold uppercase mb-1">Location</p>
                                <p className="text-sm font-medium text-gray-900 mb-2">{selectedQuery.location || selectedQuery.address}</p>
                                {selectedQuery.latitude ? (
                                    <div className="w-full h-40 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            scrolling="no"
                                            marginHeight="0"
                                            marginWidth="0"
                                            src={`https://maps.google.com/maps?q=${selectedQuery.latitude},${selectedQuery.longitude}&z=15&output=embed`}
                                            title="Map"
                                        ></iframe>
                                    </div>
                                ) : (
                                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs border border-dashed border-gray-300">
                                        No Coordinates
                                    </div>
                                )}
                            </div>

                            {/* Inspection Report — admin-uploaded files only */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Inspection Report</h3>
                                        {selectedQuery.driverName && (
                                            <span className="text-xs text-gray-500">by {selectedQuery.driverName}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                        {(selectedQuery.adminInspectionImages || []).map((img, idx) => {
                                            const url = typeof img === 'string' ? img : (img.uri || img.url);
                                            const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                            return url ? (
                                                <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                    {isPdf ? (
                                                        <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                            <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            <span className="text-xs text-red-600 font-medium">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <a href={url} target="_blank" rel="noreferrer">
                                                            <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Inspection" />
                                                        </a>
                                                    )}
                                                </div>
                                            ) : null;
                                        })}
                                        {(!selectedQuery.adminInspectionImages?.length) && <p className="text-xs text-gray-400 py-2">No inspection files uploaded yet.</p>}
                                    </div>
                                </div>
                            )}

                            {/* Quotation */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Quotation</h3>
                                        {selectedQuery.status === QUERY_STATUSES.QUOTATION_ACCEPTED && <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded">Accepted</span>}
                                    </div>

                                    {selectedQuery.quotationUrl ? (
                                        <div>
                                            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-600"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
                                                    <div>
                                                        <p className="text-sm font-bold text-blue-900">Quotation.pdf</p>
                                                        <p className="text-xs text-blue-700">{formatDate(selectedQuery.quotationUploadedAt)}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <a href={selectedQuery.quotationUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 p-1"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg></a>
                                                </div>
                                            </div>
                                            {selectedQuery.quotationAmount != null && (
                                                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-gray-50 rounded-lg">
                                                    <span className="text-sm text-gray-600">Amount:</span>
                                                    <span className="text-lg font-bold text-gray-900">{parseFloat(selectedQuery.quotationAmount).toFixed(2)} <span className="text-xs text-gray-500 font-normal">AED</span></span>
                                                </div>
                                            )}
                                            {selectedQuery.status === QUERY_STATUSES.QUOTATION_UPLOADED && (
                                                <div className="flex gap-2 mt-3">
                                                    <button onClick={handleAcceptQuotation} className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors">
                                                        Accept Quotation
                                                    </button>
                                                    <button onClick={handleRejectQuotation} className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors">
                                                        Reject Quotation
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500">No quotation uploaded yet.</p>
                                    )}
                                </div>
                            )}

                            {/* Completion Reports — admin-uploaded files only */}
                            {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) && (
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-sm font-bold text-gray-900">Work Completion Report</h3>
                                        {selectedQuery.technicianName && (
                                            <span className="text-xs text-gray-500">by {selectedQuery.technicianName}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                        {(selectedQuery.adminCompletionImages || []).map((img, idx) => {
                                            const url = typeof img === 'string' ? img : (img.uri || img.url);
                                            const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                            return url ? (
                                                <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                    {isPdf ? (
                                                        <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                            <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            <span className="text-xs text-red-600 font-medium">PDF</span>
                                                        </a>
                                                    ) : (
                                                        <a href={url} target="_blank" rel="noreferrer">
                                                            <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Completion" />
                                                        </a>
                                                    )}
                                                </div>
                                            ) : null;
                                        })}
                                        {(!selectedQuery.adminCompletionImages?.length) && <p className="text-xs text-gray-400 py-2">No completion files uploaded yet.</p>}
                                    </div>
                                </div>
                            )}
                            {(getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) || invoices.length > 0) && (
                            <div className="pt-4 border-t border-gray-100 pb-2">
                                <h3 className="text-sm font-bold text-gray-800 mb-2">Payment & Invoice</h3>
                                <div className="bg-gray-900 text-white rounded-2xl shadow-xl overflow-hidden">
                                    {/* Invoice Header */}
                                    <div className="p-5 flex justify-between items-center relative">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-gray-800 rounded-full blur-3xl -mr-10 -mt-10 opacity-50"></div>
                                        <div className="relative z-10">
                                            {invoices.length > 0 ? (
                                                <div>
                                                    <p className="text-gray-400 text-xs mb-1 font-medium">Invoice #{invoices[0].invoiceNumber || 'INV'}</p>
                                                    <span className="text-2xl font-bold tracking-tight">{selectedQuery.amount || invoices[0].totalAmount || '0'} <span className="text-sm text-gray-500 font-normal">AED</span></span>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p className="text-gray-400 text-xs mb-1 font-medium">Total Amount</p>
                                                    <span className="text-2xl font-bold tracking-tight">{selectedQuery.amount || '0'} <span className="text-sm text-gray-500 font-normal">AED</span></span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="relative z-10 flex flex-col items-end gap-1">
                                            <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${selectedQuery.status?.toLowerCase().includes('paid') ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'}`}>
                                                {selectedQuery.status?.toLowerCase().includes('paid') ? 'PAID' : 'PENDING'}
                                            </div>
                                            <button
                                                className="text-xs text-brand-golden hover:text-white underline mt-1"
                                            >
                                                {invoices.length} Invoices
                                            </button>
                                        </div>
                                    </div>

                                    {/* Actions & Proof */}
                                    <div className="bg-gray-800 p-4 border-t border-gray-700">

                                        {/* Invoice List */}
                                        {invoices.length > 0 && (
                                            <div className="space-y-2 mb-4">
                                                {invoices.map((inv) => (
                                                    <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg border border-gray-700">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center text-gray-300">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                            </div>
                                                            <div className="overflow-hidden">
                                                                <p className="text-xs font-bold text-gray-200 truncate max-w-[150px]">{inv.fileName}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <a href={inv.fileUrl} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg></a>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Payment Receipt */}
                                        <div className="mt-4 pt-4 border-t border-gray-700">
                                            <p className="text-gray-400 text-xs font-bold uppercase mb-2">Payment Receipt</p>

                                            {selectedQuery.paymentProof ? (
                                                <div className="space-y-3">
                                                    <div className="bg-gray-700 p-2 rounded-lg inline-block">
                                                        <a href={getImageUri(selectedQuery.paymentProof)} target="_blank" rel="noreferrer">
                                                            <img
                                                                src={getImageUri(selectedQuery.paymentProof)}
                                                                alt="Payment Receipt"
                                                                className="h-32 w-auto object-cover rounded border border-gray-600"
                                                            />
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 text-xs italic">
                                                    No payment receipt uploaded yet.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* After Service Section (Follow Up) */}
                            {((selectedQuery.status === QUERY_STATUSES.PAID || selectedQuery.status === QUERY_STATUSES.CLOSED || selectedQuery.followUp) || getWorkflowProgress(selectedQuery.status) >= 100) && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">After Service</h3>

                                    {selectedQuery.followUp ? (
                                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-sm font-bold text-orange-900">Reported Issue</h4>
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${selectedQuery.followUp.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-orange-200 text-orange-800'}`}>
                                                    {selectedQuery.followUp.status === 'resolved' ? 'Resolved' : 'Pending'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-800 mb-3">{selectedQuery.followUp.description}</p>

                                            {selectedQuery.followUp.images && selectedQuery.followUp.images.length > 0 && (
                                                <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                                                    {selectedQuery.followUp.images.map((img, idx) => (
                                                        <a key={idx} href={img} target="_blank" rel="noreferrer">
                                                            <img src={img} alt="Issue" className="w-16 h-16 object-cover rounded border border-orange-200" />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="text-xs text-gray-500 mb-2">
                                                Submitted on {formatDate(selectedQuery.followUp.createdAt)}
                                            </div>

                                            {selectedQuery.followUp.adminReply && (
                                                <div className="mt-3 pt-3 border-t border-orange-200">
                                                    <p className="text-xs font-bold text-orange-900 mb-1">Admin Reply</p>
                                                    <p className="text-sm text-gray-700">{selectedQuery.followUp.adminReply}</p>
                                                    {selectedQuery.followUp.replyAt && (
                                                        <p className="text-xs text-gray-400 mt-1">{formatDate(selectedQuery.followUp.replyAt)}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        canCreateFollowUp(selectedQuery) ? (
                                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center">
                                                <p className="text-sm text-gray-600 mb-3">Everything good? You can report issues within <span className="font-bold text-gray-900">{getFollowUpTimeRemaining(selectedQuery)} hours</span>.</p>
                                                <button
                                                    onClick={() => { setReportType('followup'); setIsReportModalOpen(true); }}
                                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-bold text-sm transition-colors inline-flex items-center gap-2"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                                    Report an Issue
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                <p className="text-sm text-gray-400">Service completed. Follow-up window closed.</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            )}

            {/* Report Issue Modal */}
            {isReportModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            {reportType === 'followup' ? 'Report After-Service Issue' : 'Report an Issue'}
                        </h2>
                        <form onSubmit={handleReportIssue}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Describe the issue</label>
                                <textarea
                                    value={reportDescription}
                                    onChange={(e) => setReportDescription(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none"
                                    rows="4"
                                    placeholder="Please describe the problem you are facing with this query..."
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Attachments (Optional)</label>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={handleReportImageChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                                />
                                {followUpImages.length > 0 && (
                                    <p className="text-xs text-green-600 mt-1">{followUpImages.length} images selected</p>
                                )}
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setIsReportModalOpen(false); setFollowUpImages([]); }}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                                    disabled={isUploadingReport}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 font-medium flex items-center gap-2"
                                    disabled={isUploadingReport}
                                >
                                    {isUploadingReport && <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>}
                                    {isUploadingReport ? 'Submitting...' : 'Submit Report'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PartnerQueries;
